// src/components/layout/Footer.tsx
"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Linkedin, Music2, Phone, Mail, MapPin } from "lucide-react";

export function Footer() {
  return (
    <footer className="border-t border-border/40 py-12" style={{ backgroundColor: "#001489" }}>
      <div className="container mx-auto px-4">

        {/* Top tagline */}
        <div className="text-center mb-10 space-y-2">
          <p className="text-3xl md:text-4xl font-bold" style={{ color: "white" }}>
            YOUR PROFILE. YOUR FUTURE.
          </p>
          <p className="text-lg md:text-xl font-semibold pt-2" style={{ color: "#FFFFFF" }}>
            Visibility is opportunity
          </p>
        </div>

        {/* 3-column grid: About / Links / Contact */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10 text-sm text-gray-300">

          {/* Column 1 — About */}
          <div>
            <p className="text-white font-bold text-base mb-3">Zamzi Connect (Pty) Ltd</p>
            <p className="leading-relaxed">
              South Africa&apos;s discoverability platform. We help businesses and professionals
              build search-ready, AI-ready profiles that can be found, trusted, and chosen.
            </p>
            <p className="mt-3 text-xs text-gray-400 uppercase tracking-widest">
              <span style={{ color: '#11C629', fontWeight: 'bold' }}>Be Found</span>.{" "}
              <span style={{ color: '#11C629', fontWeight: 'bold' }}>Be Trusted</span>.{" "}
              <span style={{ color: '#11C629', fontWeight: 'bold' }}>Be Chosen</span>.
            </p>
          </div>

          {/* Column 2 — Platform links */}
          <div>
            <p className="text-white font-bold text-base mb-3">Platform</p>
            <ul className="space-y-2">
              <li><Link href="/for-business-owners" className="hover:text-white transition-colors">For Business Owners</Link></li>
              <li><Link href="/fix-it" className="hover:text-white transition-colors">Fix-It Squad</Link></li>
              <li><Link href="/stays" className="hover:text-white transition-colors">Zamzi Stay</Link></li>
              <li><Link href="/craving" className="hover:text-white transition-colors">Craving</Link></li>
              <li><Link href="/retail" className="hover:text-white transition-colors">Shopaholic</Link></li>
              <li><Link href="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Column 3 — Contact & Address (E-E-A-T signals) */}
          <div>
            <p className="text-white font-bold text-base mb-3">Contact Us</p>
            <address className="not-italic space-y-3">
              <p className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#379BEB]" />
                <span>Midrand, Gauteng, South Africa</span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-[#2BC939]" />
                <a href="tel:+27774584896" className="hover:text-white transition-colors">+27 77 458 4896</a>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-[#FFB81C]" />
                <a href="mailto:connect@zamzi.co.za" className="hover:text-white transition-colors">connect@zamzi.co.za</a>
              </p>
              <p>
                <a
                  href="https://wa.me/27774584896"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#1ebe59] transition-colors"
                >
                  WhatsApp Us
                </a>
              </p>
            </address>

            {/* Social icons */}
            <div className="flex gap-3 mt-5">
              <Link href="https://www.linkedin.com/in/amanda-harrison-1330298b/" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Linkedin className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="https://www.facebook.com/share/16q516QXvH/" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Facebook className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="https://www.instagram.com/zamzi_sa" aria-label="Instagram @zamzi_sa" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Instagram className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="https://www.instagram.com/zamzi_stay" aria-label="Instagram @zamzi_stay" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Instagram className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="https://www.instagram.com/zamzidiningandeateries" aria-label="Instagram @zamzidiningandeateries" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Instagram className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="http://tiktok.com/@zamzi_sa" aria-label="TikTok @zamzi_sa" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="text-white hover:text-[#379BEB] h-8 w-8">
                  <Music2 className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Legal links row */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2 text-xs text-gray-400">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/data-deletion" className="hover:text-white transition-colors">Data Deletion</Link>
            <Link href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link href="/service-level-agreement" className="hover:text-white transition-colors">SLA</Link>
            <Link href="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link href="/about" className="hover:text-white transition-colors">About</Link>
          </div>
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Zamzi Connect (Pty) Ltd &mdash; Midrand, Gauteng &mdash; All rights reserved.
          </p>
        </div>

      </div>
    </footer>
  );
}
